import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import { useGetHarborOperatorDashboardQuery } from '@/services/HarborOperatorDashboardService';
import { useGetMediationCasesQuery } from '@/services/MediationService';
import { BookingStatus } from '@/types/booking-management.types';
import { formatFinnishCurrency } from '@/utils';
import { formatDisplayDate } from '@/utils/Date';

import {
  HarborDashboardStatusDonut,
  HarborDashboardTopHarborsChart,
  HarborDashboardTrendChart,
} from './HarborRoleDashboardCharts';

export type HarborDashboardVariant = 'marina' | 'regional';

interface QuickAction {
  label: string;
  path: string;
  icon: string;
}

const QUICK_ACTIONS: Record<HarborDashboardVariant, QuickAction[]> = {
  marina: [
    { label: 'Harbor Management', path: '/habor-management', icon: 'logistic' },
    {
      label: 'Booking Management',
      path: '/booking-management',
      icon: 'calendar',
    },
    {
      label: 'Subscription Transactions',
      path: '/subscription-transactions',
      icon: 'bill',
    },
    {
      label: 'Notifications',
      path: '/notification-management',
      icon: 'notification-status',
    },
  ],
  regional: [
    { label: 'Harbor Management', path: '/habor-management', icon: 'logistic' },
    {
      label: 'Booking Management',
      path: '/booking-management',
      icon: 'calendar',
    },
    {
      label: 'Subscription Transactions',
      path: '/subscription-transactions',
      icon: 'bill',
    },
    {
      label: 'Service Provider Management',
      path: '/service-provider-management',
      icon: 'category',
    },
    {
      label: 'Community Management',
      path: '/community-management',
      icon: 'people',
    },
    {
      label: 'Notifications',
      path: '/notification-management',
      icon: 'notification-status',
    },
  ],
};

const HARBOR_STAT_LABELS: Record<
  HarborDashboardVariant,
  { title: string; description: string }
> = {
  marina: {
    title: 'My Harbors',
    description: 'Harbors assigned to your account',
  },
  regional: {
    title: 'Harbors in Region',
    description: 'Harbors under your regional oversight',
  },
};

const getBookingStatusLabel = (status: number): string => {
  switch (status) {
    case BookingStatus.PENDING:
      return 'Payment Pending';
    case BookingStatus.CONFIRMED:
      return 'Confirmed';
    case BookingStatus.CANCELLED:
      return 'Cancelled';
    case BookingStatus.COMPLETED:
      return 'Completed';
    case BookingStatus.PENDING_CONFIRMATION:
      return 'Pending Confirmation';
    case BookingStatus.REJECTED:
      return 'Rejected';
    default:
      return 'Unknown';
  }
};

const getStatusClassName = (status: number): string => {
  switch (status) {
    case BookingStatus.CONFIRMED:
      return 'text-green-600';
    case BookingStatus.CANCELLED:
      return 'text-red-600';
    case BookingStatus.COMPLETED:
      return 'text-blue-600';
    case BookingStatus.PENDING_CONFIRMATION:
      return 'text-gray-600';
    default:
      return 'text-yellow-600';
  }
};

interface HarborRoleDashboardProps {
  variant: HarborDashboardVariant;
}

const HARBOR_SEARCH_THRESHOLD = 8;
const DEFAULT_VISIBLE_HARBORS = 10;

const HarborRoleDashboard = ({ variant }: HarborRoleDashboardProps) => {
  const navigate = useNavigate();
  const isMarina = variant === 'marina';
  const isRegional = variant === 'regional';
  const [harborSearch, setHarborSearch] = useState('');
  const [showAllHarbors, setShowAllHarbors] = useState(false);

  const { data, isLoading, isError, isFetching } =
    useGetHarborOperatorDashboardQuery({ trendMonths: 6 });

  const { data: mediationData } = useGetMediationCasesQuery({
    status: 'OPEN,AWAITING_MANAGER,AWAITING_BOATER_B',
    page: 1,
    limit: 5,
  });
  const openMediationCount = mediationData?.total ?? 0;

  const summary = data?.summary;
  const revenueTrend = useMemo(
    () => data?.revenue_trend ?? [],
    [data?.revenue_trend]
  );
  const loading = isLoading || isFetching;
  const harborLabels = HARBOR_STAT_LABELS[variant];
  const quickActions = QUICK_ACTIONS[variant];
  const hasNoHarbors = !loading && (summary?.harbor_count ?? 0) === 0;
  const harborCount = data?.harbors?.length ?? 0;
  const shouldShowHarborFilters = harborCount > HARBOR_SEARCH_THRESHOLD;

  const filteredHarbors = useMemo(() => {
    const harbors = data?.harbors ?? [];
    const query = harborSearch.trim().toLowerCase();

    if (!query) {
      return harbors;
    }

    return harbors.filter((harbor) =>
      harbor.name.toLowerCase().includes(query)
    );
  }, [data?.harbors, harborSearch]);

  const visibleHarbors = useMemo(() => {
    if (showAllHarbors || filteredHarbors.length <= DEFAULT_VISIBLE_HARBORS) {
      return filteredHarbors;
    }

    return filteredHarbors.slice(0, DEFAULT_VISIBLE_HARBORS);
  }, [filteredHarbors, showAllHarbors]);

  if (isError) {
    return (
      <Container>
        <div className="card card-grid">
          <div className="card-body">
            <p className="text-sm text-red-600">
              Failed to load dashboard data. Please try again later.
            </p>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="p-0">
      <section
        className="space-y-6"
        aria-label={
          isMarina
            ? 'Marina manager dashboard overview'
            : 'Regional operator dashboard overview'
        }
      >
        {hasNoHarbors && (
          <div className="card rounded-lg border border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-white">
            <div className="card-body py-8 text-center">
              <KeenIcon
                icon="logistic"
                className="text-3xl text-primary mx-auto mb-3"
              />
              <p className="text-sm text-gray-700 font-medium">
                {isMarina
                  ? "You don't have any harbors assigned yet."
                  : 'No harbors are currently available in your region.'}
              </p>
              <Link
                to="/habor-management"
                className="btn btn-sm btn-primary mt-4"
              >
                Go to Harbor Management
              </Link>
            </div>
          </div>
        )}

        <div
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4"
          role="region"
          aria-label="Key performance indicators"
        >
          <DashboardStatCard
            title={harborLabels.title}
            value={summary?.harbor_count ?? 0}
            description={harborLabels.description}
            icon="logistic"
            loading={loading}
            variant="primary"
          />
          <DashboardStatCard
            title="Needs Attention"
            value={summary?.needs_attention_bookings ?? 0}
            description="Pending payment or confirmation"
            icon="information-2"
            loading={loading}
            highlight={(summary?.needs_attention_bookings ?? 0) > 0}
          />
          <DashboardStatCard
            title="Berth Conflicts"
            value={openMediationCount}
            description="Extension mediation needs action"
            icon="information"
            loading={loading}
            highlight={openMediationCount > 0}
          />
          <DashboardStatCard
            title="Upcoming Stays"
            value={summary?.upcoming_bookings ?? 0}
            description="Confirmed bookings not yet ended"
            icon="calendar-tick"
            loading={loading}
          />
          <DashboardStatCard
            title="Net Revenue"
            value={summary?.net_revenue ?? 0}
            displayValue={
              loading
                ? undefined
                : formatFinnishCurrency(summary?.net_revenue ?? 0)
            }
            description="Last 6 months (berth & harbor services)"
            icon="wallet"
            loading={loading}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <DashboardStatCard
            title="Active Bookings"
            value={summary?.active_bookings ?? 0}
            description="In progress or awaiting action"
            icon="timer"
            loading={loading}
          />
          <DashboardStatCard
            title="Total Berths"
            value={summary?.total_berths ?? 0}
            description="Across your harbors"
            icon="category"
            loading={loading}
          />
          <DashboardStatCard
            title="Booking Nights"
            value={summary?.total_booking_nights ?? 0}
            description="Total nights booked (all time)"
            icon="moon"
            loading={loading}
          />
          <DashboardStatCard
            title="Completed"
            value={summary?.completed_bookings ?? 0}
            description="Successfully completed stays"
            icon="check-circle"
            loading={loading}
          />
        </div>

        {data?.harbors && data.harbors.length > 0 && (
          <div className="card rounded-lg border border-gray-500 shadow-md p-4">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Your harbors
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {shouldShowHarborFilters
                    ? `Showing ${visibleHarbors.length} of ${filteredHarbors.length} matching harbors`
                    : 'Quick access to harbor listings'}
                </p>
              </div>
              {shouldShowHarborFilters && (
                <div className="flex w-full sm:w-auto items-center gap-2">
                  <label className="input input-sm input-bordered flex items-center gap-2 w-full sm:w-72 bg-white">
                    <KeenIcon
                      icon="magnifier"
                      className="text-sm text-gray-500"
                    />
                    <input
                      type="text"
                      value={harborSearch}
                      onChange={(event) => {
                        setHarborSearch(event.target.value);
                        setShowAllHarbors(false);
                      }}
                      placeholder="Search harbors"
                      className="grow text-sm"
                      aria-label="Search harbors"
                    />
                  </label>
                  {harborSearch && (
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => {
                        setHarborSearch('');
                        setShowAllHarbors(false);
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
            </div>
            {filteredHarbors.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">
                No harbors match your search.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {visibleHarbors.map((harbor) => (
                    <Link
                      key={harbor.id}
                      to="/habor-management"
                      className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-800 hover:border-primary hover:text-primary transition-colors"
                    >
                      <KeenIcon icon="geolocation" className="text-sm" />
                      {harbor.name}
                    </Link>
                  ))}
                </div>
                {filteredHarbors.length > DEFAULT_VISIBLE_HARBORS && (
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => setShowAllHarbors((current) => !current)}
                    >
                      {showAllHarbors
                        ? 'Show fewer harbors'
                        : `Show all ${filteredHarbors.length} harbors`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div
          className="card rounded-lg border border-gray-500 shadow-md p-4"
          role="region"
          aria-label="Quick actions"
        >
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Quick actions
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Fast navigation for your most-used harbor tasks
              </p>
            </div>
            {shouldShowHarborFilters && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                {harborCount} harbors available
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            {quickActions.map((action) => (
              <Link
                key={action.path}
                to={action.path}
                className="btn btn-sm btn-light inline-flex items-center gap-2"
              >
                <KeenIcon icon={action.icon} className="text-base" />
                {action.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <HarborDashboardTrendChart
            title="Booking activity"
            subtitle="New bookings per month (last 6 months)"
            data={data?.bookings_trend ?? []}
            mode="bookings"
            loading={loading}
          />
          <HarborDashboardTrendChart
            title="Revenue trend"
            subtitle="Net revenue per month (last 6 months)"
            data={revenueTrend}
            mode="revenue"
            loading={loading}
          />
        </div>

        <div
          className={`grid grid-cols-1 gap-4 ${isRegional ? 'lg:grid-cols-2' : ''}`}
        >
          <HarborDashboardStatusDonut
            items={data?.booking_status_breakdown ?? []}
            loading={loading}
          />
          {isRegional && (
            <HarborDashboardTopHarborsChart
              items={data?.top_harbors ?? []}
              loading={loading}
            />
          )}
        </div>

        <div className="card card-grid rounded-lg border border-gray-500 text-gray-900 shadow-md p-2">
          <div className="card-header flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="card-title text-base font-semibold text-gray-900">
                Recent bookings
              </h3>
              <p className="text-xs text-gray-500">
                Latest reservations across your harbors
              </p>
            </div>
            <Link
              to="/booking-management"
              className="text-xs text-primary font-medium hover:underline"
            >
              View all
            </Link>
          </div>
          <div className="card-body overflow-x-auto">
            {loading ? (
              <p className="text-sm text-gray-600">Loading bookings...</p>
            ) : (data?.recent_bookings?.length ?? 0) === 0 ? (
              <p className="text-sm text-gray-600">No bookings yet.</p>
            ) : (
              <table className="table table-zebra w-full text-sm">
                <thead>
                  <tr>
                    <th className="font-medium text-gray-600">Guest</th>
                    <th className="font-medium text-gray-600">Harbor</th>
                    <th className="font-medium text-gray-600">Berth</th>
                    <th className="font-medium text-gray-600">Dates</th>
                    <th className="font-medium text-gray-600">Total</th>
                    <th className="font-medium text-gray-600">Status</th>
                    <th>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.recent_bookings.map((booking) => (
                    <tr key={booking.id}>
                      <td>
                        {booking.guest_name || booking.guest_email || '-'}
                      </td>
                      <td>{booking.harbor_name || '-'}</td>
                      <td>{booking.berth_number || '-'}</td>
                      <td>
                        {formatDisplayDate(
                          new Date(booking.start_date.split('T')[0])
                        )}{' '}
                        –{' '}
                        {formatDisplayDate(
                          new Date(booking.end_date.split('T')[0])
                        )}
                      </td>
                      <td>{formatFinnishCurrency(booking.total_price)}</td>
                      <td>
                        <span
                          className={`text-sm font-medium ${getStatusClassName(booking.status)}`}
                        >
                          {getBookingStatusLabel(booking.status)}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-xs btn-light"
                          onClick={() =>
                            navigate(`/booking-management/${booking.id}`)
                          }
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>
    </Container>
  );
};

export { HarborRoleDashboard };
